import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { admins, WRAPPED_PUBLIC, WRAPPED_SEASON, wrappedPromoActive } from "../Constants";
import { getWrappedStatus, trackWrapped } from "../services/WrappedService";
import { track } from "../utils/track";
import styles from "./WrappedPromo.module.css";

// Season Wrapped "have you seen yours yet?" nudge (tasks/wrapped-design.md launch mechanics).
//
// Insistent by design: it keeps coming back on every visit until the user actually VIEWS their
// wrapped (viewed=true, server-side per account, so it's silenced across all devices). Dismiss is
// only a soft snooze for the current browser session — it returns next visit. WrappedPage records
// the "view"; this component records "dismiss" (for the funnel only, not for suppression).
//
// Because it is that insistent, it is time-boxed: after WRAPPED_PROMO_LAST_DAY it stops rendering
// for everyone and the mount in App.tsx becomes a no-op. Nothing to remember to remove.

const snoozeKey = (season: number) => `wrapped_nudge_snoozed_${season}`;

const WrappedNudge = () => {
  const { t } = useTranslation("pages");
  const { user, isBeta, authLoaded } = useContext(AuthContext);
  const [show, setShow] = useState(false);

  const isAdmin = !!user && admins.includes(user.steamID);
  // Two independent gates: WHO may see it (public flag, else beta/admin preview) and WHETHER the
  // promo window is still open. The window applies to admins too — an expired nudge that still
  // only nags the three admins is just nagging the three admins.
  const gated = (WRAPPED_PUBLIC || isBeta || isAdmin) && wrappedPromoActive();

  useEffect(() => {
    if (!authLoaded || !user || !gated) return;
    // Soft snooze: dismissed earlier THIS session → stay quiet until a new session.
    try { if (sessionStorage.getItem(snoozeKey(WRAPPED_SEASON)) === "1") return; } catch { /* ignore */ }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    // Only a real VIEW silences it for good; dismissed does NOT suppress across sessions.
    getWrappedStatus(WRAPPED_SEASON).then((status) => {
      if (cancelled) return;
      if (!status || !status.eligible) return; // no/too-little data → don't nudge them to an empty Wrapped
      if (status.viewed) return;
      timer = setTimeout(() => {
        if (cancelled) return;
        setShow(true);
        track("wrapped-nudge-shown", { season: WRAPPED_SEASON });
      }, 1500); // pop in quickly — we want it noticed
    });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [authLoaded, user, gated]);

  if (!show || !user) return null;

  const dismiss = () => {
    setShow(false); // instant hide
    try { sessionStorage.setItem(snoozeKey(WRAPPED_SEASON), "1"); } catch { /* ignore */ } // quiet this session only
    trackWrapped(WRAPPED_SEASON, "dismiss"); // funnel analytics (not suppression)
    track("wrapped-nudge-dismiss", { season: WRAPPED_SEASON });
  };

  return createPortal(
    <div className={styles.nudge} role="dialog" aria-label="Season Wrapped">
      <button className={styles.nudgeClose} onClick={dismiss} aria-label="Luk">×</button>
      <div className={styles.nudgeIcon}>🎁</div>
      <div className={styles.nudgeBody}>
        <div className={styles.nudgeTitle}>{t("wrapped.nudge.title", { season: WRAPPED_SEASON })}</div>
        <div className={styles.nudgeSub}>{t("wrapped.nudge.sub")}</div>
        <Link
          className={styles.nudgeBtn}
          to="/wrapped"
          onClick={() => { setShow(false); track("wrapped-nudge-click", { season: WRAPPED_SEASON }); }}
        >
          {t("wrapped.nudge.cta")}
        </Link>
      </div>
    </div>,
    document.body
  );
};

export default WrappedNudge;
